import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row, Input, Label, FormGroup } from 'reactstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from 'components/Button';
import Form from 'components/Form';
import { useForm } from 'react-hook-form';
import TextField from 'components/TextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useApiDelete, useApiGet, useApiPost, useApiPut } from 'hooks/useApi';
import useFieldSetErrors from 'hooks/useFieldSetErrors';
import { useToast } from 'hooks/useToast';
import { Loader } from 'react-bootstrap-typeahead';
import Modal from 'components/Modal';
import ToastType from 'enum/ToastType';
import AdminCustomer, { CustomerType } from 'models/AdminCustomer';
import SelectField from 'components/SelectField';

const schema = yup
  .object({
    name: yup.string().required('O nome é obrigatório.'),
    email: yup.string().email().required('O e-mail é obrigatório.'),
    phone: yup.string().default(''),
    cellPhone: yup.string().default(''),
    cpf: yup.string().default(''),
    type: yup
      .mixed<CustomerType>()
      .oneOf(['PF', 'PJ'])
      .required('O tipo é obrigatório.')
  })
  .required();

const AdminCustomersFormPage = () => {
  const params = useParams();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showPurgeConfirmation, setShowPurgeConfirmation] = useState(false);
  const [purgeConfirmationName, setPurgeConfirmationName] = useState('');
  const navigate = useNavigate();
  const { addToast } = useToast();

  const customerId = params.id;

  const { data, isLoading: isFetching } = useApiGet<AdminCustomer>(
    `/admin/customers`,
    { id: customerId, enabled: !!customerId }
  );

  const { mutate: mutateCreate, isPending: isCreating } = useApiPost<
    AdminCustomer
  >('/admin/customers');
  const { mutate: mutateUpdate, isPending: isUpdating } = useApiPut<
    AdminCustomer
  >('/admin/customers', customerId);
  const { mutate: mutateDelete, isPending: isDeleting } = useApiDelete(
    '/admin/customers',
    customerId
  );

  const { mutate: mutatePurge, isPending: isPurging } = useApiPost(
    `/admin/customers/${customerId}/purge`
  );

  const isSaving = isCreating || isUpdating;
  const mutate = customerId ? mutateUpdate : mutateCreate;

  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: 'PF' as CustomerType
    }
  });

  const onError = useFieldSetErrors(formMethods.setError);

  useEffect(() => {
    if (!data) return;
    formMethods.reset({
      name: data.name,
      email: data.email,
      phone: data.phone || '',
      cellPhone: data.cellPhone || '',
      cpf: data.cpf || '',
      type: data.type
    } as any);
  }, [data, formMethods]);

  const typeOptions = useMemo(
    () => [
      { value: 'PF', label: 'Pessoa Física' },
      { value: 'PJ', label: 'Pessoa Jurídica' }
    ],
    []
  );

  const handleOnSuccess = useCallback(() => {
    addToast(ToastType.SUCCESS, 'Cliente salvo com sucesso');
    navigate('/admin/customers');
  }, [addToast, navigate]);

  const handleOnSubmit = useCallback(
    (payload: any) => {
      mutate(payload as any, {
        onError,
        onSuccess: handleOnSuccess
      });
    },
    [mutate, onError, handleOnSuccess]
  );

  const handleOnDelete = useCallback(() => {
    if (!customerId) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao excluir cliente'
        );
      },
      onSuccess: () => {
        addToast(ToastType.SUCCESS, 'Cliente excluído com sucesso');
        navigate('/admin/customers');
      }
    });
  }, [addToast, customerId, mutateDelete, navigate]);

  const handleOnPurge = useCallback(() => {
    if (!customerId) return;
    mutatePurge({ confirmationName: purgeConfirmationName } as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() ||
            'Erro ao excluir integralmente o cliente'
        );
      },
      onSuccess: () => {
        addToast(
          ToastType.SUCCESS,
          'Exclusão integral iniciada. Os dados serão removidos em segundo plano.'
        );
        setShowPurgeConfirmation(false);
        setPurgeConfirmationName('');
        navigate('/admin/customers');
      }
    });
  }, [addToast, customerId, mutatePurge, navigate, purgeConfirmationName]);

  return (
    <>
      <div className="padding-page">
        <Form onSubmit={handleOnSubmit} {...formMethods}>
          <Row className="d-flex flex-column flex-md-row">
            <Col className="col-12 col-md">
              <div>
                <p className="text-gray-600">
                  <Link
                    to="/admin/customers"
                    className="text-decoration-none text-gray-600"
                  >
                    Clientes
                  </Link>
                  {' / '}
                  {customerId ? 'Editar' : 'Novo'}
                </p>
                <h4 className="fw-bold">
                  {customerId ? 'Editar Cliente' : 'Novo Cliente'}
                </h4>
                <span className="small-typography text-gray-600">
                  Configure as informações do cliente.
                </span>
              </div>
            </Col>
            <Col className="col-12 col-md align-text mt-3 mt-md-0 d-flex gap-2 justify-content-end">
              {customerId && (
                <Button
                  type="button"
                  color="danger"
                  loading={isDeleting}
                  icon="trash"
                  onClick={() => setShowDeleteConfirmation(true)}
                >
                  Excluir
                </Button>
              )}
              <Button
                color="primary"
                icon="floppy"
                loading={isSaving}
                disabled={!formMethods.formState.isValid}
              >
                Salvar
              </Button>
            </Col>
          </Row>

          {isFetching ? (
            <div className="text-center p-5">
              <Loader />
            </div>
          ) : (
            <div className="bg-white rounded shadow p-4 mt-4">
              <Row>
                <Col md="6">
                  <TextField
                    name="name"
                    label="Nome:"
                    placeholder="Nome do cliente"
                  />
                </Col>
                <Col md="6">
                  <TextField
                    name="email"
                    label="E-mail:"
                    placeholder="E-mail"
                  />
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <TextField
                    name="phone"
                    label="Telefone:"
                    placeholder="Telefone"
                  />
                </Col>
                <Col md="4">
                  <TextField
                    name="cellPhone"
                    label="Celular:"
                    placeholder="Celular"
                  />
                </Col>
                <Col md="4">
                  <TextField
                    name="cpf"
                    label="CPF/CNPJ:"
                    placeholder="CPF ou CNPJ"
                  />
                </Col>
              </Row>
              <Row>
                <Col md="4">
                  <SelectField
                    name="type"
                    label="Tipo:"
                    options={typeOptions}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Form>

        {customerId && !isFetching && (
          <div
            className="rounded mt-4 p-4"
            style={{
              border: '1px solid #dc3545',
              backgroundColor: '#fff5f5'
            }}
          >
            <h5 className="text-danger fw-bold">Zona de Perigo</h5>
            <p className="mb-2 small">
              A exclusão integral remove <strong>permanentemente</strong> o
              cliente e <strong>todos</strong> os dados associados: mídias,
              dispositivos, players, playlists, campanhas, notícias, licenças,
              usuários, empresas e configurações.
            </p>
            <p className="mb-3 small text-danger fw-bold">
              Esta ação é irreversível e não pode ser desfeita.
            </p>
            <Button
              type="button"
              color="danger"
              icon="trash"
              onClick={() => setShowPurgeConfirmation(true)}
            >
              Excluir integralmente
            </Button>
          </div>
        )}
      </div>

      <Modal
        primaryButtonAction={handleOnDelete}
        primaryButtonLabel="Excluir"
        title="Atenção!"
        isOpen={showDeleteConfirmation}
        showCloseButton={false}
        toggle={() => setShowDeleteConfirmation((x) => !x)}
        secondaryButtonAction={() => setShowDeleteConfirmation(false)}
        secondaryButtonLabel="Cancelar"
        isLoading={isDeleting}
      >
        <p>
          Deseja realmente excluir o cliente? <br />
          <small>Essa alteração não poderá ser revertida.</small>
        </p>
      </Modal>

      <Modal
        primaryButtonAction={handleOnPurge}
        primaryButtonLabel="Excluir permanentemente"
        title="⚠️ Exclusão integral irreversível"
        isOpen={showPurgeConfirmation}
        showCloseButton={false}
        toggle={() => {
          setShowPurgeConfirmation((x) => !x);
          setPurgeConfirmationName('');
        }}
        secondaryButtonAction={() => {
          setShowPurgeConfirmation(false);
          setPurgeConfirmationName('');
        }}
        secondaryButtonLabel="Cancelar"
        isLoading={isPurging}
        disabledPrimaryButton={purgeConfirmationName !== data?.name}
      >
        <p>
          Esta ação excluirá <strong>permanentemente</strong> o cliente{' '}
          <strong>{data?.name}</strong> e <strong>todos</strong> os dados
          associados (mídias, dispositivos, playlists, campanhas, notícias,
          licenças, usuários).
        </p>
        <p className="text-danger fw-bold">
          Esta operação é irreversível e não pode ser desfeita.
        </p>
        <FormGroup>
          <Label className="small">
            Para confirmar, digite o nome do cliente:{' '}
            <strong>{data?.name}</strong>
          </Label>
          <Input
            type="text"
            value={purgeConfirmationName}
            onChange={(e) => setPurgeConfirmationName(e.target.value)}
            placeholder={data?.name}
          />
        </FormGroup>
      </Modal>
    </>
  );
};

export default AdminCustomersFormPage;
